/**
 * TemplateApplyModal — Sprint 4.6
 *
 * Fluxo:
 *   1. Mostra resumo do template
 *   2. Operador escolhe alvo (device_ids ou group/customer/region/tag)
 *   3. Configura stagger + force + filtros
 *   4. Botão "Preview (dry-run)" → mostra targets, commands, skipped, risks
 *   5. Se riscos detectados (wifi/network) → checkbox "Confirmar — pode desconectar"
 *   6. Botão "Aplicar" → cria comandos reais
 *   7. Mostra resultado final + link p/ ver run
 *
 * Aceita devices pré-selecionados via prop initialDeviceIds (vindo do BulkActionBar).
 */
import { useEffect, useState } from "react";
import {
  applyConfigTemplate,
  applyFilteredConfigTemplate,
  diffConfigTemplate,
  diffConfigTemplateBulk,
  listAllDevices,
  listCustomers,
  listGroups,
  listFleetRegions,
} from "../../lib/api.js";
import { useCriticalConfirm } from "../ConfirmCriticalAction.jsx";
import { t } from "../../i18n/index.js";

export default function TemplateApplyModal({
  open, template, initialDeviceIds = null, onClose, onApplied
}) {
  // S3.10C1 — confirmação forte para aplicar template (config crítica).
  const confirmCritical = useCriticalConfirm();
  // Modo do seletor: 'ids' (search/lista) | 'group' | 'customer' | 'region' | 'manual'
  const [selectorMode, setSelectorMode] = useState("ids");

  // === Device search (Sprint 4.6.5) ===
  const [deviceSearch, setDeviceSearch] = useState("");
  const [deviceResults, setDeviceResults] = useState([]);     // [{device_id, ...}]
  const [deviceSelected, setDeviceSelected] = useState(new Set()); // set de device_id
  const [deviceLoading, setDeviceLoading] = useState(false);
  // Modo manual escondido (paste UUID)
  const [manualIdsText, setManualIdsText] = useState("");

  // === Grupos/Clientes/Regiões — listas e seleção (Sprint 4.6.5) ===
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [groupSearch, setGroupSearch] = useState("");

  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");

  const [regionOptions, setRegionOptions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [regionSearch, setRegionSearch] = useState("");

  // Filtros
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [offlineOnly, setOfflineOnly] = useState(false);
  const [minFw, setMinFw] = useState("");

  // Apply opts
  const [staggerSeconds, setStaggerSeconds] = useState(0);
  // Sprint 4.6.5 — command_stagger_seconds (default 90s, evita perda de ACK)
  const [commandStagger, setCommandStagger] = useState(90);
  const [force, setForce] = useState(false);
  const [confirmRisky, setConfirmRisky] = useState(false);

  // Preview / resultado
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);
  // Sprint 4.9 — diff por device antes do apply
  const [diffData, setDiffData] = useState(null);
  const [diffBusy, setDiffBusy] = useState(false);
  // Sprint 4.9.2 — filtros para apply-filtered
  const [filterIncludeOnlyChanged, setFilterIncludeOnlyChanged] = useState(false);
  const [filterIncludeOnlyRisky, setFilterIncludeOnlyRisky] = useState(false);
  const [filterExcludeWithoutSnapshot, setFilterExcludeWithoutSnapshot] = useState(false);
  const [filterExcludeIdentical, setFilterExcludeIdentical] = useState(false);
  // Manual override: Set<device_id>. Vazio = sem override.
  const [manualSelectedDevices, setManualSelectedDevices] = useState(new Set());

  // Init
  useEffect(() => {
    if (!open) return;
    setSelectorMode(initialDeviceIds ? "ids" : "ids");
    setDeviceSearch("");
    setDeviceSelected(new Set(initialDeviceIds || []));
    setDeviceResults([]);
    setManualIdsText("");
    setGroupId(""); setCustomerId(""); setRegionId("");
    setGroupSearch(""); setCustomerSearch(""); setRegionSearch("");
    setOnlineOnly(false); setOfflineOnly(false); setMinFw("");
    setStaggerSeconds(0); setCommandStagger(90); setForce(false); setConfirmRisky(false);
    setPreview(null); setAppliedResult(null); setErr(null);
    setDiffData(null); setDiffBusy(false);
    setFilterIncludeOnlyChanged(false); setFilterIncludeOnlyRisky(false);
    setFilterExcludeWithoutSnapshot(false); setFilterExcludeIdentical(false);
    setManualSelectedDevices(new Set());
    // Carrega opções iniciais (limitado pelo backend)
    listCustomers().then(r => setCustomerOptions(r?.customers || [])).catch(() => {});
    listGroups().then(r => setGroupOptions(r?.groups || [])).catch(() => {});
    listFleetRegions().then(r => setRegionOptions(r?.regions || [])).catch(() => {});
  }, [open, initialDeviceIds]);

  // Sprint 4.6.5 — Debounce search de devices
  useEffect(() => {
    if (!open || selectorMode !== "ids") return;
    const t = setTimeout(() => {
      setDeviceLoading(true);
      const params = { limit: 100 };
      if (deviceSearch.trim()) params.search = deviceSearch.trim();
      if (onlineOnly) params.status = "online";
      else if (offlineOnly) params.status = "offline";
      listAllDevices(params)
        .then(devs => setDeviceResults(Array.isArray(devs) ? devs : []))
        .catch(() => setDeviceResults([]))
        .finally(() => setDeviceLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [open, selectorMode, deviceSearch, onlineOnly, offlineOnly]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !template) return null;

  const buildSelector = () => {
    const sel = {};
    if (selectorMode === "ids") {
      // Combina seleção visual + UUIDs manuais (modo avançado)
      const fromSelected = Array.from(deviceSelected);
      const fromManual = manualIdsText
        .split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      const ids = Array.from(new Set([...fromSelected, ...fromManual]));
      if (ids.length === 0) throw new Error("Selecione ao menos um device (use busca ou cole UUIDs manualmente).");
      sel.device_ids = ids;
    } else if (selectorMode === "group") {
      if (!groupId.trim()) throw new Error("Escolha um grupo na lista.");
      sel.group_id = groupId.trim();
    } else if (selectorMode === "customer") {
      if (!customerId.trim()) throw new Error("Escolha um cliente na lista.");
      sel.customer_id = customerId.trim();
    } else if (selectorMode === "region") {
      if (!regionId.trim()) throw new Error("Escolha uma região na lista.");
      sel.region_id = regionId.trim();
    }
    if (onlineOnly)  sel.online_only = true;
    if (offlineOnly) sel.offline_only = true;
    if (minFw.trim()) sel.min_fw_version = minFw.trim();
    return sel;
  };

  const doApply = async (dryRun) => {
    // S3.10C1 — aplicar de verdade (não-dry-run) exige confirmação forte.
    let confirm;
    if (!dryRun) {
      const secret = await confirmCritical({
        actionLabel: "Aplicar template (real)",
        target: {},
        impact:
          "Vai ENFILEIRAR comandos de configuração nos dispositivos selecionados " +
          "(pode alterar Wi-Fi/rede/watchdog/relé).",
      });
      if (secret == null) return;
      confirm = secret;
    }
    setBusy(true); setErr(null);
    if (dryRun) setPreview(null); else setAppliedResult(null);
    try {
      const selector = buildSelector();
      const body = {
        selector,
        dry_run: dryRun,
        stagger_seconds: Number(staggerSeconds) || 0,
        command_stagger_seconds: Number(commandStagger) || 90,  // Sprint 4.6.5
        force,
        confirm_risky: confirmRisky,
      };
      const r = await applyConfigTemplate(template.id, body, { confirm });
      if (dryRun) setPreview(r);
      else {
        setAppliedResult(r);
        onApplied?.(r);
      }
    } catch (e) {
      // 409 confirm_risky → mostra mensagem amigável
      if (e?.status === 409) {
        setErr("Esta ação pode desconectar dispositivos (config_wifi ou config_network). " +
               "Marque 'Confirmar — entendo os riscos' e tente novamente.");
      } else if (e?.status === 413) {
        setErr(e?.detail?.message ||
               "Limite de 200 devices por lote excedido. Refine o seletor ou aplique em lotes menores.");
      } else {
        setErr(formatErrorApply(e));
      }
    } finally {
      setBusy(false);
    }
  };

  // Sprint 4.9 → 4.9.1 — Preview Diff funciona em TODOS os modos:
  // device_ids, group_id, customer_id, region_id, tag_ids
  // (antes da 4.9.1 era só 'ids'). Reusa buildSelector do apply.
  const doDiff = async () => {
    setDiffBusy(true); setErr(null); setDiffData(null);
    try {
      const selector = buildSelector();
      // Validação rápida: pelo menos um seletor
      const hasAny =
        (selector.device_ids?.length || 0) > 0 ||
        selector.customer_id ||
        selector.group_id ||
        selector.region_id ||
        (selector.tag_ids?.length || 0) > 0;
      if (!hasAny) {
        throw new Error("Selecione ao menos 1 device/customer/group/region/tag.");
      }
      const r = await diffConfigTemplateBulk(template.id, selector);
      setDiffData(r);
    } catch (e) {
      setErr(formatErrorApply(e));
    } finally {
      setDiffBusy(false);
    }
  };

  // Sprint 4.9.2 — Apply só nos devices que passam pelos filtros + manual select.
  // Modo 'use_diff_filters': usa os 4 toggles abaixo do diff.
  // Modo 'use_manual_only': usa só manual_selected (Set).
  // Pode combinar: filtros + manual_selected = INTERSECT (backend cuida).
  const doApplyFiltered = async (manualOnly = false) => {
    // S3.10C1 — apply-filtered (em massa) exige confirmação forte.
    const secret = await confirmCritical({
      actionLabel: "Aplicar template filtrado (em massa)",
      target: {},
      impact:
        "Vai ENFILEIRAR comandos de configuração em VÁRIOS dispositivos de uma vez.",
    });
    if (secret == null) return;
    setBusy(true); setErr(null); setAppliedResult(null);
    try {
      const selector = buildSelector();
      const payload = {
        selector,
        include_only_changed: manualOnly ? false : filterIncludeOnlyChanged,
        include_only_risky:   manualOnly ? false : filterIncludeOnlyRisky,
        exclude_without_snapshot: manualOnly ? false : filterExcludeWithoutSnapshot,
        exclude_identical: manualOnly ? false : filterExcludeIdentical,
        selected_device_ids: manualOnly
          ? Array.from(manualSelectedDevices)
          : (manualSelectedDevices.size > 0 ? Array.from(manualSelectedDevices) : []),
        dry_run: false,
        stagger_seconds: Number(staggerSeconds) || 0,
        command_stagger_seconds: Number(commandStagger) || 90,
        force,
        confirm_risky: confirmRisky,
      };
      const r = await applyFilteredConfigTemplate(template.id, payload, { confirm: secret });
      setAppliedResult({
        // Adapta para formato esperado por ResultSection
        ...(r.apply_result || {
          run_id: r.run_id,
          template_id: r.template_id,
          dry_run: false,
          status: r.selected_count > 0 ? 'completed' : 'failed',
          target_count: r.selected_count,
          created_commands: 0,
          skipped_devices: r.target_count - r.selected_count,
          targets: [], commands: [], skipped: [], warnings: [], risks: [],
        }),
        // Sprint 4.9.2 — campos adicionais do apply-filtered
        _filtered: {
          target_count: r.target_count,
          selected_count: r.selected_count,
          skipped_identical: r.skipped_identical,
          skipped_without_snapshot: r.skipped_without_snapshot,
          skipped_not_risky: r.skipped_not_risky,
          skipped_manual: r.skipped_manual,
          skipped_compat: r.skipped_compat,
        },
      });
      onApplied?.(r.apply_result);
    } catch (e) {
      if (e?.status === 409) {
        setErr("Esta ação pode desconectar dispositivos. Marque 'Confirmar — entendo os riscos' e tente novamente.");
      } else if (e?.status === 413) {
        setErr(e?.detail?.message ||
               "Limite de 200 devices por lote excedido. Refine o seletor ou aplique em lotes menores.");
      } else {
        setErr(formatErrorApply(e));
      }
    } finally {
      setBusy(false);
    }
  };

  // Toggle manual seleção de 1 device
  const toggleManualDevice = (deviceId) => {
    const newSet = new Set(manualSelectedDevices);
    if (newSet.has(deviceId)) newSet.delete(deviceId);
    else newSet.add(deviceId);
    setManualSelectedDevices(newSet);
  };

  // Helpers visuais
  const manualCount = manualSelectedDevices.size;
  const hasAnyFilter =
    filterIncludeOnlyChanged || filterIncludeOnlyRisky ||
    filterExcludeWithoutSnapshot || filterExcludeIdentical;

  // Sprint 4.6.2 — formatador robusto que prioriza 'errors' (detalhe)
  // antes de 'error' (texto genérico tipo 'invalid_payload').
  function formatErrorApply(e) {
    if (!e) return "Erro desconhecido";
    if (typeof e === "string") return e;
    const data = e.data || e.body;
    if (data && typeof data === "object") {
      const detail = data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        return detail.map(d => {
          const loc = Array.isArray(d.loc)
            ? d.loc.filter(x => x !== "body" && (typeof x === "string" || typeof x === "number")).join(".")
            : "";
          const msg = d.msg || d.message || JSON.stringify(d);
          return loc ? `${loc}: ${msg}` : msg;
        }).join("\n");
      }
      if (detail && typeof detail === "object") {
        // PRIORIZA errors detalhado antes de error genérico
        if (detail.errors && typeof detail.errors === "object") {
          const lines = [];
          for (const [k, v] of Object.entries(detail.errors)) {
            if (Array.isArray(v)) {
              for (const item of v) {
                if (typeof item === "string") {
                  // tenta parsear repr pydantic
                  const msgM = item.match(/'msg':\s*(["'])((?:\\.|(?!\1).)*)\1/);
                  const locM = item.match(/'loc':\s*[\(\[]([^\)\]]+)[\)\]]/);
                  const field = locM
                    ? locM[1].split(",").map(p => p.trim().replace(/^['"]|['"]$/g, "")).filter(p => p && p !== "body").join(".")
                    : "";
                  const msg = msgM ? msgM[2] : item;
                  lines.push(field ? `${k}.${field}: ${msg}` : `${k}: ${msg}`);
                } else {
                  lines.push(`${k}: ${JSON.stringify(item)}`);
                }
              }
            } else if (typeof v === "string") {
              lines.push(`${k}: ${v}`);
            }
          }
          if (lines.length > 0) return lines.join("\n");
        }
        if (detail.message) return String(detail.message);
        if (detail.error)   return String(detail.error);
        try { return JSON.stringify(detail); } catch { return String(detail); }
      }
    }
    if (e.message) return String(e.message);
    try { return JSON.stringify(e); } catch { return String(e); }
  }

  // F1 (agenda) — trava de lote ≤ MAX_APPLY_DEVICES (espelha o 413 do backend).
  // Usa a melhor contagem conhecida: resolvida (diff/dry-run) ou seleção 'ids'.
  const MAX_APPLY_DEVICES = 200;
  const idsKnownCount = selectorMode === "ids"
    ? new Set([
        ...Array.from(deviceSelected),
        ...manualIdsText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
      ]).size
    : null;
  const knownTargetCount =
    (typeof diffData?.target_count === "number" ? diffData.target_count : null) ??
    (typeof preview?.target_count === "number" ? preview.target_count : null) ??
    idsKnownCount;
  const overApplyLimit =
    typeof knownTargetCount === "number" && knownTargetCount > MAX_APPLY_DEVICES;
  const templateHasSchedule = !!template?.payload_json?.config_schedule;

  const showRiskyConfirm = preview?.risks?.length > 0 ||
                          hasRisky(template.payload_json);

  return (
    <div className="fixed inset-0 z-[2100] bg-black/70 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-3xl flex flex-col"
           style={{ maxHeight: "92vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent">
            🚀 Aplicar Template
            <span className="ml-2 text-noc-accent font-mono">{template.name}</span>
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-noc-accent/30 bg-noc-accent/10">
              {template.scope}
            </span>
          </h3>
          <button type="button" onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Conteúdo do template */}
          <div className="bg-noc-bg/40 border border-noc-accent/30 rounded p-2 text-[10px]">
            <div className="text-noc-accent uppercase tracking-wide mb-1">
              Comandos que serão enviados
            </div>
            <div className="font-mono text-noc-text">
              {Object.keys(template.payload_json || {}).join(", ")}
            </div>
          </div>

          {/* Selector mode */}
          {!appliedResult && (
            <>
              <div className="flex gap-1 text-[11px] border-b border-noc-border">
                {[
                  { id: "ids",      label: "Device IDs" },
                  { id: "group",    label: "Grupo" },
                  { id: "customer", label: "Cliente" },
                  { id: "region",   label: "Região GIS" },
                ].map(m => (
                  <button key={m.id} type="button"
                    onClick={() => setSelectorMode(m.id)}
                    className={`px-3 py-1.5 border-b-2 ${
                      selectorMode === m.id
                        ? "border-noc-accent/30 text-noc-accent"
                        : "border-transparent text-noc-muted hover:text-noc-text"
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>

              {selectorMode === "ids" && (
                <div className="space-y-2">
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={deviceSearch}
                      onChange={(e) => setDeviceSearch(e.target.value)}
                      placeholder="Buscar device por id, cliente, grupo, tag..."
                      className="flex-1 bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs text-noc-text"
                    />
                    <span className="text-[10px] text-noc-accent font-mono whitespace-nowrap">
                      {deviceSelected.size} sel.
                    </span>
                  </div>
                  <div className="flex gap-1 text-[10px]">
                    <button type="button"
                      onClick={() => {
                        const next = new Set(deviceSelected);
                        deviceResults.forEach(d => next.add(d.device_id));
                        setDeviceSelected(next);
                      }}
                      className="px-2 py-0.5 rounded border border-noc-border text-noc-muted hover:text-noc-text">
                      Selecionar todos filtrados ({deviceResults.length})
                    </button>
                    <button type="button"
                      onClick={() => setDeviceSelected(new Set())}
                      className="px-2 py-0.5 rounded border border-noc-border text-noc-muted hover:text-noc-err">
                      Limpar seleção
                    </button>
                  </div>
                  <div className="border border-noc-border rounded max-h-48 overflow-y-auto bg-noc-bg/30">
                    {deviceLoading && (
                      <div className="text-[10px] text-noc-muted/70 p-2">Buscando...</div>
                    )}
                    {!deviceLoading && deviceResults.length === 0 && (
                      <div className="text-[10px] text-noc-muted/70 p-2 italic">
                        {deviceSearch ? "Nenhum device encontrado." : "Digite para buscar ou marque os filtros online/offline."}
                      </div>
                    )}
                    {deviceResults.map(d => {
                      const sel = deviceSelected.has(d.device_id);
                      return (
                        <label key={d.device_id}
                          className={`flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer border-b border-noc-border/30 ${sel ? "bg-noc-accent/10" : "hover:bg-noc-bg/50"}`}>
                          <input type="checkbox" checked={sel}
                            onChange={(e) => {
                              const next = new Set(deviceSelected);
                              if (e.target.checked) next.add(d.device_id);
                              else next.delete(d.device_id);
                              setDeviceSelected(next);
                            }} />
                          <span className="font-mono text-noc-accent flex-1">{d.device_id}</span>
                          <span className="text-noc-muted/70 text-[9px]">
                            fw {d.fw_version || "?"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <details className="text-[10px]">
                    <summary className="text-noc-muted cursor-pointer">
                      ⚙ Colar device_ids manualmente (UUIDs ou códigos)
                    </summary>
                    <textarea value={manualIdsText}
                      onChange={(e) => setManualIdsText(e.target.value)}
                      rows={3} placeholder="IPWD-A1B2C3D4&#10;IPWD-E5F6G7H8"
                      className="w-full mt-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-[11px] text-noc-text" />
                  </details>
                </div>
              )}
              {selectorMode === "group" && (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    placeholder="Filtrar grupos..."
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs text-noc-text"
                  />
                  <div className="border border-noc-border rounded max-h-48 overflow-y-auto bg-noc-bg/30">
                    {groupOptions
                      .filter(g => !groupSearch || g.name?.toLowerCase().includes(groupSearch.toLowerCase()))
                      .map(g => {
                        const sel = groupId === String(g.id);
                        return (
                          <label key={g.id}
                            className={`flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer border-b border-noc-border/30 ${sel ? "bg-noc-accent/10" : "hover:bg-noc-bg/50"}`}>
                            <input type="radio" name="group" checked={sel}
                              onChange={() => setGroupId(String(g.id))} />
                            <span className="text-noc-text flex-1">{g.name}</span>
                            <span className="text-noc-muted/70 text-[9px]">
                              {g.devices_count ?? 0} devs
                            </span>
                          </label>
                        );
                      })}
                    {groupOptions.length === 0 && (
                      <div className="text-[10px] text-noc-muted/70 p-2 italic">
                        Nenhum grupo cadastrado.
                      </div>
                    )}
                  </div>
                  <details className="text-[10px]">
                    <summary className="text-noc-muted cursor-pointer">⚙ Group ID UUID manual</summary>
                    <input value={groupId} onChange={(e) => setGroupId(e.target.value)}
                      placeholder="UUID..." className="w-full mt-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-[11px] text-noc-text" />
                  </details>
                </div>
              )}
              {selectorMode === "customer" && (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Filtrar clientes..."
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs text-noc-text"
                  />
                  <div className="border border-noc-border rounded max-h-48 overflow-y-auto bg-noc-bg/30">
                    {customerOptions
                      .filter(cli => {
                        if (!customerSearch) return true;
                        const q = customerSearch.toLowerCase();
                        return (cli.name?.toLowerCase().includes(q) ||
                                cli.code?.toLowerCase().includes(q));
                      })
                      .map(cli => {
                        const sel = customerId === String(cli.id);
                        return (
                          <label key={cli.id}
                            className={`flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer border-b border-noc-border/30 ${sel ? "bg-noc-accent/10" : "hover:bg-noc-bg/50"}`}>
                            <input type="radio" name="cust" checked={sel}
                              onChange={() => setCustomerId(String(cli.id))} />
                            <span className="text-noc-text flex-1">{cli.name}</span>
                            <span className="text-noc-muted/70 text-[9px]">{cli.code}</span>
                            <span className="text-noc-muted/70 text-[9px]">
                              {cli.devices_count ?? 0} devs
                            </span>
                          </label>
                        );
                      })}
                    {customerOptions.length === 0 && (
                      <div className="text-[10px] text-noc-muted/70 p-2 italic">
                        Nenhum cliente cadastrado.
                      </div>
                    )}
                  </div>
                  <details className="text-[10px]">
                    <summary className="text-noc-muted cursor-pointer">⚙ Customer ID UUID manual</summary>
                    <input value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                      placeholder="UUID..." className="w-full mt-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-[11px] text-noc-text" />
                  </details>
                </div>
              )}
              {selectorMode === "region" && (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={regionSearch}
                    onChange={(e) => setRegionSearch(e.target.value)}
                    placeholder="Filtrar regiões..."
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs text-noc-text"
                  />
                  <div className="border border-noc-border rounded max-h-48 overflow-y-auto bg-noc-bg/30">
                    {regionOptions
                      .filter(r => !regionSearch || r.name?.toLowerCase().includes(regionSearch.toLowerCase()))
                      .map(r => {
                        const sel = regionId === String(r.id);
                        return (
                          <label key={r.id}
                            className={`flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer border-b border-noc-border/30 ${sel ? "bg-noc-accent/10" : "hover:bg-noc-bg/50"}`}>
                            <input type="radio" name="region" checked={sel}
                              onChange={() => setRegionId(String(r.id))} />
                            <span className="text-noc-text flex-1">{r.name}</span>
                          </label>
                        );
                      })}
                    {regionOptions.length === 0 && (
                      <div className="text-[10px] text-noc-muted/70 p-2 italic">
                        Nenhuma região cadastrada.
                      </div>
                    )}
                  </div>
                  <details className="text-[10px]">
                    <summary className="text-noc-muted cursor-pointer">⚙ Region ID UUID manual</summary>
                    <input value={regionId} onChange={(e) => setRegionId(e.target.value)}
                      placeholder="UUID..." className="w-full mt-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-[11px] text-noc-text" />
                  </details>
                </div>
              )}

              {/* Filtros */}
              <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={onlineOnly}
                    onChange={(e) => { setOnlineOnly(e.target.checked); if (e.target.checked) setOfflineOnly(false); }} />
                  <span>Só online</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={offlineOnly}
                    onChange={(e) => { setOfflineOnly(e.target.checked); if (e.target.checked) setOnlineOnly(false); }} />
                  <span>Só offline</span>
                </label>
                <label className="block space-y-0.5 col-span-2">
                  <span className="text-noc-muted">Firmware mínimo (opcional)</span>
                  <input value={minFw} onChange={(e) => setMinFw(e.target.value)}
                    placeholder="ex: 2.7.0"
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
                </label>
              </div>

              {/* Opções apply */}
              <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                <label className="block space-y-0.5">
                  <span className="text-noc-muted">Stagger (segundos entre devices)</span>
                  <input type="number" value={staggerSeconds} min={0} max={3600}
                    onChange={(e) => setStaggerSeconds(e.target.value)}
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
                </label>
                {/* Sprint 4.6.5 — segundos entre comandos do mesmo device */}
                <label className="block space-y-0.5">
                  <span className="text-noc-muted">Stagger entre comandos (mesmo device)</span>
                  <input type="number" value={commandStagger} min={0} max={600}
                    onChange={(e) => setCommandStagger(e.target.value)}
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
                </label>
                <label className="flex items-center gap-2 col-span-2">
                  <input type="checkbox" checked={force}
                    onChange={(e) => setForce(e.target.checked)} />
                  <span title="Ignora check de firmware compatível">
                    Force (ignora compat fw)
                  </span>
                </label>
              </div>

              {/* Sprint 4.6.5 — Aviso de sequenciamento */}
              <div className="text-[10px] text-noc-accent bg-noc-accent/10 border border-noc-accent/30 rounded p-2 mt-1">
                💡 Comandos serão aplicados em sequência para evitar perda de ACK:
                <span className="font-mono ml-1">
                  wifi → network → management → watchdog → schedule → advanced
                </span>
                <br/>Com command-stagger de {commandStagger || 90}s entre cada comando do mesmo device.
              </div>
            </>
          )}

          {/* Sprint 4.9 — Diff por device */}
          {diffData && !appliedResult && (
            <DiffSection
              diff={diffData}
              applyFilters={{
                includeOnlyChanged: filterIncludeOnlyChanged,
                setIncludeOnlyChanged: setFilterIncludeOnlyChanged,
                includeOnlyRisky: filterIncludeOnlyRisky,
                setIncludeOnlyRisky: setFilterIncludeOnlyRisky,
                excludeWithoutSnapshot: filterExcludeWithoutSnapshot,
                setExcludeWithoutSnapshot: setFilterExcludeWithoutSnapshot,
                excludeIdentical: filterExcludeIdentical,
                setExcludeIdentical: setFilterExcludeIdentical,
              }}
              manualSelected={manualSelectedDevices}
              onToggleManual={toggleManualDevice}
              onClearManual={() => setManualSelectedDevices(new Set())}
            />
          )}

          {/* Preview */}
          {preview && !appliedResult && (
            <PreviewSection preview={preview} />
          )}

          {/* Result */}
          {appliedResult && (
            <ResultSection result={appliedResult} />
          )}

          {/* Confirm risky */}
          {!appliedResult && showRiskyConfirm && (
            <div className="bg-noc-warn/10 border border-noc-warn/30 rounded p-2 text-xs">
              <div className="text-noc-warn font-bold mb-1">⚠ Ação sensível</div>
              <div className="text-noc-warn mb-2">
                Este template altera Wi-Fi ou rede e pode desconectar dispositivos.
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={confirmRisky}
                  onChange={(e) => setConfirmRisky(e.target.checked)} />
                <span>Confirmar — entendo os riscos</span>
              </label>
            </div>
          )}

          {err && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
              {err}
            </div>
          )}
        </div>

        {!appliedResult && typeof knownTargetCount === "number" && (
          <div className={`px-4 py-2 text-xs border-t ${overApplyLimit
            ? "border-noc-err/30 bg-noc-err/10 text-noc-err"
            : "border-noc-warn/30 bg-noc-warn/10 text-noc-warn"}`}>
            {overApplyLimit ? (
              <span>⛔ <b>{knownTargetCount}</b> devices resolvidos — acima do limite de{" "}
                <b>{MAX_APPLY_DEVICES}</b> por lote. O backend recusa (413).
                Refine o seletor (cliente/grupo/região/tag) ou aplique em lotes menores.</span>
            ) : (
              <span>🎯 <b>{knownTargetCount}</b> device(s) no alvo (limite {MAX_APPLY_DEVICES}/lote).</span>
            )}
            {templateHasSchedule && (
              <div className="mt-1 opacity-80">
                ⚠️ Agenda com <b>reinício de relé</b> em massa pode reiniciar muitos dispositivos
                ao mesmo tempo (corte de energia simultâneo). Prefira lotes menores / horários distintos.
              </div>
            )}
          </div>
        )}
        <div className="px-4 py-3 border-t border-noc-border flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text">
            Fechar
          </button>
          {!appliedResult && (
            <>
              <button type="button" onClick={doDiff} disabled={diffBusy || busy}
                className="px-3 py-1.5 text-xs rounded border border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10 disabled:opacity-50"
                title={t("template.previewDiff")}>
                {diffBusy ? t("common.loading") : t("template.previewDiff")}
              </button>
              <button type="button" onClick={() => doApply(true)} disabled={busy}
                className="px-3 py-1.5 text-xs rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10 disabled:opacity-50">
                {busy ? t("common.busy") : t("template.previewDryRun")}
              </button>
              <button type="button" onClick={() => doApply(false)} disabled={busy || overApplyLimit}
                title={overApplyLimit ? `Acima do limite de ${MAX_APPLY_DEVICES} devices por lote` : undefined}
                className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-50">
                {busy ? t("common.busy") : t("template.applyAll")}
              </button>
              {diffData && hasAnyFilter && (
                <button type="button" onClick={() => doApplyFiltered(false)} disabled={busy}
                  className="px-3 py-1.5 text-xs rounded bg-noc-warn/15 hover:bg-noc-warn/25 border border-noc-warn/40 text-noc-warn font-semibold disabled:opacity-50"
                  title={t("template.applyFiltered")}>
                  {busy ? t("common.busy") : t("template.applyFiltered")}
                </button>
              )}
              {diffData && manualCount > 0 && (
                <button type="button" onClick={() => doApplyFiltered(true)} disabled={busy}
                  className="px-3 py-1.5 text-xs rounded bg-noc-purple/15 hover:bg-noc-purple/25 border border-noc-purple/40 text-noc-purple font-semibold disabled:opacity-50"
                  title={t("template.applySelected", { n: manualCount })}>
                  {busy ? t("common.busy") : t("template.applySelected", { n: manualCount })}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function hasRisky(payload) {
  return !!(payload && (payload.config_wifi || payload.config_network));
}

function PreviewSection({ preview }) {
  // Sprint 4.6.5 — calcula offset legível em segundos relativo ao primeiro comando
  const firstTs = preview.commands.find(c => c.scheduled_at)?.scheduled_at;
  const firstMs = firstTs ? new Date(firstTs).getTime() : null;
  const computeOffset = (sched) => {
    if (!sched) return "imediato";
    if (!firstMs) return sched.substring(11, 19);
    const diff = Math.round((new Date(sched).getTime() - firstMs) / 1000);
    // Sprint 4.6.6 — T+0 é o primeiro comando da sequência (não "imediato" — é
    // o T-zero relativo ao apply, base do staggering)
    if (diff === 0) return "T+0";
    if (diff < 60) return `+${diff}s`;
    if (diff < 3600) return `+${Math.floor(diff/60)}m${diff%60 ? (diff%60)+"s" : ""}`;
    return `+${Math.floor(diff/3600)}h${Math.floor((diff%3600)/60)}m`;
  };
  // Identifica comandos arriscados
  const riskyTypes = new Set(["config_wifi", "config_network"]);

  return (
    <div className="space-y-2 mt-2">
      <div className="text-xs font-semibold text-noc-accent uppercase">📊 Preview (dry-run)</div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <Stat label="Targets" value={preview.target_count} />
        <Stat label="Cmds" value={preview.commands.length} highlight={preview.commands.length > 0} />
        <Stat label="Skipped" value={preview.skipped_devices} warn={preview.skipped_devices > 0} />
      </div>
      {preview.risks?.length > 0 && (
        <div className="text-xs bg-noc-warn/10 border border-noc-warn/30 rounded p-2 space-y-1">
          <div className="text-noc-warn uppercase tracking-wide">Riscos detectados</div>
          {preview.risks.map((r, i) => (
            <div key={i} className="text-noc-warn">• {r}</div>
          ))}
        </div>
      )}
      {preview.warnings?.length > 0 && (
        <div className="text-[10px] bg-noc-bg/40 border border-noc-border/60 rounded p-2 space-y-0.5 max-h-24 overflow-y-auto">
          <div className="text-noc-muted uppercase tracking-wide">Warnings</div>
          {preview.warnings.map((w, i) => (
            <div key={i} className="text-noc-text/70 font-mono">{w}</div>
          ))}
        </div>
      )}
      {preview.skipped?.length > 0 && (
        <details className="text-[10px]">
          <summary className="text-noc-muted cursor-pointer">
            Devices skipped ({preview.skipped.length})
          </summary>
          <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
            {preview.skipped.slice(0, 50).map((s, i) => (
              <div key={i} className="font-mono text-[10px]">
                <span className="text-noc-err">{s.device_id}</span>
                <span className="text-noc-muted/70 ml-2">{s.reason}</span>
              </div>
            ))}
          </div>
        </details>
      )}
      {/* Sprint 4.6.5 — Tabela de comandos */}
      <details className="text-[10px]" open>
        <summary className="text-noc-muted cursor-pointer">
          📋 Comandos planejados ({preview.commands.length}) — ordem por device
        </summary>
        <div className="mt-1 border border-noc-border rounded overflow-hidden">
          <table className="w-full text-[10px]">
            <thead className="bg-noc-panel2/30 text-noc-muted uppercase tracking-wide">
              <tr>
                <th className="px-2 py-1 text-left">Device</th>
                <th className="px-2 py-1 text-left">Comando</th>
                <th className="px-2 py-1 text-left">Offset</th>
                <th className="px-2 py-1 text-left">Risco</th>
                <th className="px-2 py-1 text-left">Status previsto</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {preview.commands.slice(0, 200).map((c, i) => (
                <tr key={i} className="border-t border-noc-border/30 hover:bg-noc-bg/30">
                  <td className="px-2 py-0.5 text-noc-accent">{c.device_id}</td>
                  <td className="px-2 py-0.5 text-noc-text">{c.type}</td>
                  <td className="px-2 py-0.5 text-noc-ok">{computeOffset(c.scheduled_at)}</td>
                  <td className="px-2 py-0.5">
                    {riskyTypes.has(c.type) ? (
                      <span className="text-noc-warn">⚠ desconecta</span>
                    ) : (
                      <span className="text-noc-muted/60">—</span>
                    )}
                  </td>
                  <td className="px-2 py-0.5 text-noc-muted/70">
                    {c.scheduled_at ? "scheduled" : "pending"}
                  </td>
                </tr>
              ))}
              {preview.commands.length > 200 && (
                <tr><td colSpan="5" className="px-2 py-1 text-noc-muted italic">
                  ... +{preview.commands.length - 200} comandos não exibidos
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function ResultSection({ result }) {
  const isOk = result.status === "completed";
  return (
    <div className="space-y-2 mt-2">
      <div className="text-xs font-semibold text-noc-accent uppercase">✅ Resultado</div>
      <div className={`text-xs px-2 py-1.5 rounded border ${
        isOk
          ? "bg-noc-ok/10 border-noc-ok/30 text-noc-ok"
          : result.status === "partial"
          ? "bg-noc-warn/10 border-noc-warn/30 text-noc-warn"
          : "bg-noc-err/10 border-noc-err/30 text-noc-err"
      }`}>
        Status: <span className="font-bold">{result.status.toUpperCase()}</span>
        <span className="ml-3">{result.created_commands} comandos criados</span>
        {result.skipped_devices > 0 && (
          <span className="ml-3">{result.skipped_devices} devices ignorados</span>
        )}
      </div>
      <div className="text-[10px] text-noc-muted font-mono">
        run_id: {result.run_id}
      </div>
    </div>
  );
}

function Stat({ label, value, warn, highlight }) {
  return (
    <div className={`bg-noc-bg/40 border rounded p-2 ${
      warn ? "border-noc-warn/30" : highlight ? "border-noc-accent/30" : "border-noc-border"
    }`}>
      <div className="text-[10px] text-noc-muted uppercase tracking-wide">{label}</div>
      <div className={`font-mono mt-0.5 text-sm ${
        warn ? "text-noc-warn" : highlight ? "text-noc-accent" : "text-noc-text"
      }`}>
        {value}
      </div>
    </div>
  );
}

// ============================================================================
// Sprint 4.9 — DiffSection: mostra o que mudará por device
// ============================================================================

function SeverityBadge({ severity }) {
  const cfg = {
    new:     { color: "emerald", label: "NOVO" },
    changed: { color: "amber",   label: "ALTERADO" },
    risky:   { color: "red",     label: "RISCO" },
  }[severity] || { color: "noc", label: severity };
  return (
    <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${
      cfg.color === "emerald" ? "border-noc-ok/30 text-noc-ok bg-noc-ok/10" :
      cfg.color === "amber"   ? "border-noc-warn/30 text-noc-warn bg-noc-warn/10" :
      cfg.color === "red"     ? "border-noc-err/30 text-noc-err bg-noc-err/10" :
                                "border-noc-border text-noc-muted"
    }`}>
      {cfg.label}
    </span>
  );
}

function formatVal(v) {
  if (v === null || v === undefined) return <span className="text-noc-muted/60 italic">(vazio)</span>;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function DiffSection({ diff, applyFilters, manualSelected, onToggleManual, onClearManual }) {
  // diff vem do endpoint /diff/bulk (Sprint 4.9.1) com risk_summary + métricas
  // agregadas. Mantém compat com response antiga (Sprint 4.9): apenas
  // devices[] e devices_without_snapshot — campos a mais default a undefined.
  const totalChanges = diff.devices.reduce((s, d) => s + (d.total_changes || 0), 0);
  const totalRisky = diff.devices.reduce(
    (s, d) => s + (d.risky_changes?.length || 0), 0);
  const targetCount = diff.target_count ?? diff.devices.length;
  const withChanges = diff.devices_with_changes;
  const noChanges = diff.devices_no_changes;
  const noSnap = diff.devices_without_snapshot ?? 0;
  const rs = diff.risk_summary;  // pode ser undefined em response Sprint 4.9

  // Sprint 4.9.1 — filtros locais
  const [filterRisky, setFilterRisky] = useState(false);
  const [filterChanged, setFilterChanged] = useState(false);
  const [filterNoSnap, setFilterNoSnap] = useState(false);

  const filtered = diff.devices.filter(d => {
    if (filterRisky && (d.risky_changes?.length || 0) === 0) return false;
    if (filterChanged && (d.total_changes || 0) === 0) return false;
    if (filterNoSnap && d.has_snapshot) return false;
    return true;
  });

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs font-semibold text-noc-ok uppercase">
          🔬 {t("diff.titleHeader")} — {diff.template_name}
        </div>
        <div className="text-[10px] text-noc-muted">
          {targetCount} device(s) · {totalChanges} mudança(s)
          {totalRisky > 0 && (
            <span className="text-noc-err"> · {totalRisky} risco(s)</span>
          )}
          {noSnap > 0 && (
            <span className="text-noc-warn"> · {noSnap} sem snapshot</span>
          )}
        </div>
      </div>

      {/* Risk summary (Sprint 4.9.1) */}
      {rs && targetCount > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 text-[10px]">
          <RiskCounter label={t("diff.ssidChanges")}          n={rs.ssid_changes}          color="red" />
          <RiskCounter label={t("diff.passwordChanges")}      n={rs.password_changes}      color="red" />
          <RiskCounter label={t("diff.dhcpToStatic")}         n={rs.dhcp_to_static}        color="red" />
          <RiskCounter label={t("diff.staticToDhcp")}         n={rs.static_to_dhcp}        color="amber" />
          <RiskCounter label={t("diff.hostnameChanges")}      n={rs.hostname_changes}      color="red" />
          <RiskCounter label={t("diff.gatewayChanges")}       n={rs.gateway_changes}       color="red" />
          <RiskCounter label={t("diff.scheduleChanges")}      n={rs.schedule_changes}      color="amber" />
          <RiskCounter label={t("diff.adminPasswordChanges")} n={rs.admin_password_changes} color="red" />
          <RiskCounter label={t("diff.ipChanges")}            n={rs.ip_changes}            color="amber" />
        </div>
      )}

      {/* Resumo with/no changes */}
      {(withChanges !== undefined || noChanges !== undefined) && (
        <div className="flex items-center gap-3 text-[10px]">
          {withChanges !== undefined && (
            <span className="text-noc-warn">
              ✏ {withChanges} {t("template.withChanges")}
            </span>
          )}
          {noChanges !== undefined && noChanges > 0 && (
            <span className="text-noc-ok">
              ✓ {noChanges} {t("template.alreadyIdentical").toLowerCase()}
            </span>
          )}
        </div>
      )}

      {/* Sprint 4.9.2 — Filtros para Apply (somente se applyFilters passado) */}
      {applyFilters && (
        <div className="bg-noc-bg/30 border border-noc-warn/30 rounded p-2 space-y-1.5">
          <div className="text-[10px] text-noc-warn uppercase tracking-wide font-semibold">
            {t("template.filterApplyBlock")}
          </div>
          <div className="grid grid-cols-2 gap-1 text-[11px]">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={applyFilters.includeOnlyChanged}
                onChange={(e) => applyFilters.setIncludeOnlyChanged(e.target.checked)} />
              <span className={applyFilters.includeOnlyChanged ? "text-noc-ok" : "text-noc-muted"}>
                {t("template.onlyChanged")}
              </span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={applyFilters.includeOnlyRisky}
                onChange={(e) => applyFilters.setIncludeOnlyRisky(e.target.checked)} />
              <span className={applyFilters.includeOnlyRisky ? "text-noc-err" : "text-noc-muted"}>
                {t("template.onlyRisky")}
              </span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={applyFilters.excludeWithoutSnapshot}
                onChange={(e) => applyFilters.setExcludeWithoutSnapshot(e.target.checked)} />
              <span className={applyFilters.excludeWithoutSnapshot ? "text-noc-warn" : "text-noc-muted"}>
                {t("template.excludeWithoutSnapshot")}
              </span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={applyFilters.excludeIdentical}
                onChange={(e) => applyFilters.setExcludeIdentical(e.target.checked)} />
              <span className={applyFilters.excludeIdentical ? "text-noc-ok" : "text-noc-muted"}>
                {t("template.excludeIdentical")}
              </span>
            </label>
          </div>
          {manualSelected && manualSelected.size > 0 && (
            <div className="flex items-center gap-2 text-[10px] pt-1 border-t border-noc-border/40">
              <span className="text-noc-purple">
                ✓ {t("template.selectedManually", { n: manualSelected.size })}
              </span>
              <button type="button" onClick={onClearManual}
                className="text-noc-muted hover:text-noc-err underline ml-auto">
                {t("template.clearSelection")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sprint 4.9.1 — filtros VISUAIS (esconde cards na tela) */}
      {targetCount > 1 && (
        <div className="flex items-center gap-3 text-[10px] py-1.5 border-y border-noc-border/40">
          <span className="text-noc-muted uppercase tracking-wide">{t("template.filterViewBlock")}:</span>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={filterRisky}
              onChange={(e) => setFilterRisky(e.target.checked)} />
            <span className={filterRisky ? "text-noc-err" : "text-noc-muted"}>
              {t("template.onlyRisks")}
            </span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={filterChanged}
              onChange={(e) => setFilterChanged(e.target.checked)} />
            <span className={filterChanged ? "text-noc-warn" : "text-noc-muted"}>
              {t("template.onlyWithChanges")}
            </span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={filterNoSnap}
              onChange={(e) => setFilterNoSnap(e.target.checked)} />
            <span className={filterNoSnap ? "text-noc-warn" : "text-noc-muted"}>
              {t("template.onlyWithoutSnapshot")}
            </span>
          </label>
          {filtered.length !== diff.devices.length && (
            <span className="text-noc-muted/70 ml-auto">
              {filtered.length} {t("common.of")} {diff.devices.length}
            </span>
          )}
        </div>
      )}

      {filtered.map((dev) => (
        <DeviceDiffCard key={dev.device_id} dev={dev}
          manualChecked={manualSelected?.has(dev.device_id) || false}
          onToggleManual={onToggleManual}
        />
      ))}

      {filtered.length === 0 && targetCount > 0 && (
        <div className="text-[11px] text-noc-muted italic py-2">
          {t("template.noDeviceMatches")}
        </div>
      )}
    </div>
  );
}

function RiskCounter({ label, n, color }) {
  const dim = n === 0;
  const cls =
    color === "red"   ? "border-noc-err/30 text-noc-err"   :
    color === "amber" ? "border-noc-warn/30 text-noc-warn" :
                        "border-noc-border text-noc-muted";
  return (
    <div className={`border ${cls} rounded px-1.5 py-1 ${dim ? "opacity-30" : ""}`}>
      <div className="text-noc-muted/80 text-[9px] uppercase tracking-wide">
        {label}
      </div>
      <div className="font-mono font-bold text-sm">{n}</div>
    </div>
  );
}

function DeviceDiffCard({ dev, manualChecked, onToggleManual }) {
  const noChanges = (dev.total_changes || 0) === 0;
  const hasRisky = (dev.risky_changes?.length || 0) > 0;
  return (
    <details
      open={!noChanges}
      className={`border rounded ${
        hasRisky ? "border-noc-err/30 bg-noc-err/10"
        : noChanges ? "border-noc-ok/30 bg-noc-ok/10"
        : "border-noc-warn/30 bg-noc-warn/10"
      }`}
    >
      <summary className="px-2 py-1.5 cursor-pointer flex items-center gap-2 text-xs">
        {/* Sprint 4.9.2 — checkbox manual */}
        {onToggleManual && (
          <input type="checkbox" checked={manualChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleManual(dev.device_id)}
            title="Marcar para 'Aplicar Selecionados'"
            className="cursor-pointer"
          />
        )}
        <span className="font-mono text-noc-accent">{dev.device_id}</span>
        {dev.fw_version && (
          <span className="text-[10px] text-noc-muted/70">fw {dev.fw_version}</span>
        )}
        {!dev.has_snapshot && (
          <span className="text-[10px] text-noc-warn">⚠ sem snapshot</span>
        )}
        {noChanges ? (
          <span className="text-[10px] text-noc-ok ml-auto">nada a mudar</span>
        ) : (
          <span className="text-[10px] ml-auto">
            <span className="text-noc-muted">{dev.total_changes} mudança(s)</span>
            {hasRisky && (
              <span className="text-noc-err ml-2 font-bold">
                ⚠ {dev.risky_changes.length} risco(s)
              </span>
            )}
          </span>
        )}
      </summary>

      <div className="px-3 pb-2 space-y-1.5">
        {dev.warnings?.length > 0 && (
          <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-1.5">
            {dev.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
        )}

        {dev.sections.map((sec) => (
          <div key={sec.section} className="text-[11px]">
            <div className="text-noc-muted uppercase tracking-wide text-[9px] mb-1">
              {sec.section}
              {sec.snapshot_unavailable && (
                <span className="ml-1 text-noc-warn">(sem snapshot)</span>
              )}
            </div>
            <div className="space-y-0.5">
              {sec.changes.map((ch, i) => (
                <div key={i} className="flex items-center gap-2 font-mono text-[10px] py-0.5">
                  <SeverityBadge severity={ch.severity} />
                  <span className="text-noc-text">{ch.label}:</span>
                  <span className="text-noc-muted/70 line-through">{formatVal(ch.current_value)}</span>
                  <span className="text-noc-muted">→</span>
                  <span className={
                    ch.severity === "risky"   ? "text-noc-err font-bold"
                    : ch.severity === "changed" ? "text-noc-warn"
                    : "text-noc-ok"
                  }>{formatVal(ch.new_value)}</span>
                  {ch.note && (
                    <span className="text-noc-muted/60 italic ml-1">— {ch.note}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {noChanges && dev.has_snapshot && (
          <div className="text-[11px] text-noc-ok italic">
            Snapshot atual já casa com o template — nenhuma mudança será gerada.
          </div>
        )}
      </div>
    </details>
  );
}
